<template>
  <div>
    <div
      class="card mb-3"
      style="width: 100%"
      v-loading="loading"
      :element-loading-text="loadingText"
      element-loading-spinner="el-icon-loading"
      element-loading-background="rgba(0, 0, 0, 0.8)"
    >
      <div class="card-header bg-light">
        <h3 class="title fw-normal mb-0 p-2">AI Settings</h3>
      </div>

      <div class="p-5">
        <el-collapse v-model="activeNames" accordion>
          <el-collapse-item name="1" title="AI Button Settings">
            <div class="icon-picker-grid">
              <div class="text-sm">Icon</div>
              <div class="text-sm">Label</div>
              <div class="icon-cell">
                <el-button @click="openIconPicker('ai_buttonSettings')" size="mini">
                  <template v-if="ai_settings.ai_buttonSettings.icon">
                    <icons :iconName="ai_settings.ai_buttonSettings.icon"></icons>
                  </template>
                  <template v-else>
                    <img :src="aiIconSrc" alt="AI Icon" class="w-[25px] h-[25px]" />
                  </template>
                </el-button>
              </div>

              <div class="label-cell">
                <el-input
                  v-model="ai_settings.ai_buttonSettings.buttonLabel"
                  placeholder="Enter label"
                  class="w-1/2"
                />
              </div>
            </div>
          </el-collapse-item>

          <el-collapse-item name="2" title="Messages">
            <div class="icon-picker-grid">
              <div class="text-sm">Icon</div>
              <div class="text-sm">Chat Approval Title</div>

              <div>
                <el-button @click="openIconPicker('messages')" size="mini">
                  <template v-if="ai_settings.messages.icon">
                    <icons :iconName="ai_settings.messages.icon"></icons>
                  </template>
                  <template v-else>
                    <img :src="aiIconSrc" alt="AI Icon" class="w-[25px] h-[25px]" />
                  </template>
                </el-button>
              </div>

              <div>
                <el-input
                  v-model="ai_settings.messages.buttonLabel"
                  placeholder="Enter label"
                  class="width-54"
                />
              </div>
            </div>

            <div class="flex flex-col gap-2.5 mt-3">
              <div class="flex flex-col gap-1">
                <label class="text-sm">User Input Placeholder</label>
                <el-input
                  v-model="ai_settings.messages.userInputPlaceholder"
                  placeholder="Enter user input placeholder"
                  class="width-60"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label>Welcome Message</label>
                <el-input
                  v-model="ai_settings.messages.welcomeMsg"
                  placeholder="Enter welcome message"
                  class="width-60"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label>Error Message</label>
                <el-input
                  v-model="ai_settings.messages.errorMsg"
                  placeholder="Enter error message"
                  class="width-60"
                />
              </div>

              <div class="flex flex-col gap-1">
                <label>Limit Hit Message</label>
                <el-input
                  v-model="ai_settings.messages.limitMsg"
                  placeholder="Enter limit hit message"
                  class="width-60"
                />
              </div>
            </div>

            <div class="flex justify-between items-center py-2 w-[30%]">
              <span>Disclaimer</span>
              <el-switch v-model="ai_settings.messages.disclaimer" />
            </div>

            <div class="flex flex-col gap-1" v-if="ai_settings.messages.disclaimer">
              <el-input
                v-model="ai_settings.messages.additionalMessage"
                placeholder="Enter additional message"
                class="width-60"
              />
            </div>
          </el-collapse-item>

          <el-collapse-item name="3" title="AI Settings">
            <div class="flex flex-col gap-1">
              <label class="text-sm">Instructions</label>
              <el-input
                :style="{ fontSize: '12px' }"
                type="textarea"
                :rows="4"
                v-model="ai_settings.aiSettings.instructions"
                placeholder="Share any relevant information, context, or rules the AI should know to help your users"
              />
            </div>

            <div class="flex flex-col gap-1 mt-1">
              <label class="text-sm">Personality & Response Style</label>
              <el-input
                :style="{ fontSize: '12px' }"
                type="textarea"
                :rows="4"
                v-model="ai_settings.aiSettings.personalityStyle"
                placeholder="Tone of voice, response length, general vibe etc..."
              />
            </div>
          </el-collapse-item>

          <el-collapse-item name="4" title="Suggested Prompts">
            <div class="heading">Pre-defined prompts users can click on</div>
            <div class="flex justify-between items-center py-1.5 w-[30%]">
              <span>Let AI Suggest</span>
              <el-switch v-model="ai_settings.suggestedPrompts.letAISuggest" />
            </div>

            <div v-if="!ai_settings.suggestedPrompts.letAISuggest">
              <label class="prompts">Prompts</label>
              <div
                v-for="(prompt, index) in ai_settings.suggestedPrompts.prompts"
                :key="index"
                class="flex items-center gap-2"
              >
                <el-input
                  v-model="ai_settings.suggestedPrompts.prompts[index]"
                  placeholder="Ex: Show me pending tasks?"
                />
                <i
                  class="el-icon-delete cursor-pointer text-red-500"
                  @click="removePrompt(index)"
                ></i>
              </div>

              <el-button
                icon="el-icon-circle-plus"
                @click="addPrompt"
                style="margin-top: 8px"
                :size="'small'"
              >
                Add
              </el-button>
            </div>
          </el-collapse-item>

          <el-collapse-item name="5" title="Apperance">
            <div class="flex justify-between items-center py-1.5 w-[30%]">
              <span>User Avatar</span>
              <el-switch v-model="ai_settings.appearance.userAvatarEnabled" />
            </div>

            <div class="flex justify-between items-center py-1.5 w-[30%]">
              <span>AI Message Bubble</span>
              <el-switch v-model="ai_settings.appearance.aiMessageBubbleEnabled" />
            </div>

            <div class="flex justify-between items-center py-1.5 w-[30%]">
              <span>User Message Bubble</span>
              <el-switch v-model="ai_settings.appearance.userMessageBubbleEnabled" />
            </div>

            <div class="flex items-center gap-6">
              <span>Message Style</span>
              <el-radio-group
                v-model="ai_settings.appearance.messageStyle"
                size="mini"
                fill="#409EFF"
              >
                <el-radio-button label="Modern"></el-radio-button>
                <el-radio-button label="Round"></el-radio-button>
                <el-radio-button label="Sharp"></el-radio-button>
              </el-radio-group>
            </div>
          </el-collapse-item>
        </el-collapse>
        <div class="flex justify-end gap-2.5 mt-2">
          <!-- Cancel Button -->
          <el-button
            size="mini"
            type="default"
            class="bg-transparent border text-black text-xs 2xl:text-mds hover:text-brand-500 hover:border-brand-500"
            @click="resetSettings"
          >
            Cancel
          </el-button>

          <el-button
            size="mini"
            type="primary"
            class="!bg-[var(--brand-500)] !text-white !border-brand-500 text-xs 2xl:text-mds"
            @click="saveSettings"
          >
            Save
          </el-button>
        </div>
      </div>
    </div>

    <!-- Icon Picker Dialog -->
    <dialog-component
      :title="'Select Menu Icon'"
      :visible="iconPickerdialogVisible"
      @before-close="closeIconPopUp"
      :containerWidth="'77%'"
      :containerMaxHeight="'80%'"
      :isShowFooter="false"
    >
      <icons-list @icon-data="setIconData"></icons-list>
    </dialog-component>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  export default {
    name: 'AISettings',
    data() {
      return {
        aiIconSrc: require('@/assets/ai-icon.svg'),
        companyDetails: {},
        loading: false,
        loadingText: 'Loading...',
        activeNames: '',
        iconPickerdialogVisible: false,
        selectedIcon: '',
        selectedMessageIcon: '',
        currentIconTarget: '',
        ai_settings: {
          ai_buttonSettings: {
            buttonLabel: 'Ask AI',
          },
          messages: {
            buttonLabel: 'Ask AI',
            userInputPlaceholder: 'Ask Anything....!',
            welcomeMsg: 'How can I help you today?',
            errorMsg: 'Something went wrong. Please try again',
            limitMsg: 'Chat limit reached. Please try again later',
            disclaimer: true,
            additionalMessage: 'AI can make mistakes.Please double-check responses',
          },
          aiSettings: {
            instructions: '',
            personalityStyle: '',
          },
          suggestedPrompts: {
            letAISuggest: true,
            prompts: [''],
          },
          appearance: {
            userAvatarEnabled: true,
            aiMessageBubbleEnabled: false,
            userMessageBubbleEnabled: false,
            messageStyle: 'Modern',
          },
        },
        savedAISettings: {},
      };
    },
    mounted() {
      if (this.getCompanyDetails) {
        this.companyDetails = { ...this.getCompanyDetails };
        if (
          this.companyDetails.ai_settings &&
          Object.keys(this.companyDetails.ai_settings).length > 0
        ) {
          this.ai_settings = JSON.parse(JSON.stringify(this.companyDetails.ai_settings));
        }
        this.savedAISettings = JSON.parse(JSON.stringify(this.ai_settings));
      }
    },

    computed: {
      ...mapGetters('company', ['getCompanyDetails', 'getUpdateCompany']),
    },
    methods: {
      openIconPicker(target) {
        this.currentIconTarget = target;
        this.iconPickerdialogVisible = true;
      },
      setIconData(name) {
        if (this.currentIconTarget === 'ai_buttonSettings') {
          this.ai_settings.ai_buttonSettings.icon = name;
        } else if (this.currentIconTarget === 'messages') {
          this.ai_settings.messages.icon = name;
        }

        this.iconPickerdialogVisible = false;
      },

      closeIconPopUp() {
        this.iconPickerdialogVisible = false;
      },
      addPrompt() {
        if (this.ai_settings.suggestedPrompts.prompts.length < 3) {
          this.ai_settings.suggestedPrompts.prompts.push('');
        } else {
          this.$message.info('You can add up to 3 prompts only.');
        }
      },
      removePrompt(index) {
        if (index === 0) {
          this.$message.info('The first prompt cannot be removed.');
          return;
        }
        this.ai_settings.suggestedPrompts.prompts.splice(index, 1);
      },
      async saveSettings() {
        this.loading = true;
        try {
          this.companyDetails['ai_settings'] = { ...this.ai_settings };
          if (this.companyDetails?._id) {
            await this.$store.dispatch('company/updateCompany', this.companyDetails);
            if (this.getUpdateCompany.success) {
              await this.$store.dispatch('company/fetchCompany', this.companyDetails._id);
              this.ai_settings = JSON.parse(JSON.stringify(this.getCompanyDetails.ai_settings));
              this.savedAISettings = JSON.parse(JSON.stringify(this.ai_settings));
              this.$notify.success({
                title: 'Success',
                message: 'AI Settings saved successfully',
              });
            } else {
              this.$notify.error({
                title: 'Error',
                message: this.getUpdateCompany.message,
              });
            }
          }
        } catch (err) {
          console.error('Error saving AI Settings:', err);
          this.$notify.error({
            title: 'Error',
            message: 'Something went wrong while saving AI Settings',
          });
        } finally {
          this.loading = false;
        }
      },
      resetSettings() {
        this.ai_settings = JSON.parse(JSON.stringify(this.savedAISettings));
      },
    },
  };
</script>

<style lang="scss" scoped>
  .sidemenu {
    max-height: 510px;
    overflow-y: auto;
    border: 1px ridge;
    margin-right: 5px;
    padding: 10px;
  }

  .el-collapse-item__content {
    color: #666;
    line-height: 1.6;
  }

  .el-collapse .el-collapse-item {
    padding: 5px 10px;
  }
  :deep(.el-collapse .el-collapse-item .el-collapse-item__wrap) {
    border: none !important;
    padding: 0px 10px !important;
  }
  .icon-picker-grid {
    display: grid;
    grid-template-columns: 120px 1fr;
    grid-template-rows: auto auto;
    gap: 8px 12px;
    align-items: center;
    padding: 10px 0;
  }
  .width-54 {
    width: 54% !important;
  }
  .width-60 {
    width: 60% !important;
  }
  .card-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
</style>
